import React, { useState } from 'react';
import { Handshake, Copy, Check, ArrowRight, MousePointerClick, Users, Wallet, BadgeDollarSign } from 'lucide-react';
import { OsCard, AnimatedNumber, Skeleton } from '../theme';
import AgentAdviceRail from '../../components/AgentAdviceRail';

/* Affiliate earnings center — link, clicks, referrals, commissions. */

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function Stat({ icon: Icon, label, children, delay = 0 }) {
  return (
    <OsCard className="p-4 os3-rise" style={{ animationDelay: `${delay}ms` }}>
      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: 'var(--os-ink-faint)' }}>
        <Icon className="w-3.5 h-3.5" style={{ color: '#0EA5E9' }} /> {label}
      </p>
      <p className="mt-1.5 text-[24px] font-black tabular-nums leading-tight" style={{ color: 'var(--os-ink)' }}>{children}</p>
    </OsCard>
  );
}

export default function AffiliateHome({ affiliate, isEs, enter }) {
  const [copied, setCopied] = useState(false);
  const [copiedDemo, setCopiedDemo] = useState(false);
  if (!affiliate) return <div className="mt-6 grid grid-cols-2 gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const link = `https://kitxens.com/?ref=${affiliate.ref_code}`;
  const demoLink = `https://kitxens.com/restaurant/demo?ref=${affiliate.ref_code}`;
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  const copyDemo = () => { navigator.clipboard.writeText(demoLink); setCopiedDemo(true); setTimeout(() => setCopiedDemo(false), 1800); };
  return (
    <div data-testid="affiliate-home">
      <OsCard className="mt-6 p-5" glow data-testid="affiliate-link-card">
        <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold" style={{ color: '#0EA5E9' }}>
          <Handshake className="w-3.5 h-3.5" /> {isEs ? 'Tu link de referido' : 'Your referral link'}
          <span className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-black"
            style={{ background: affiliate.status === 'approved' || affiliate.status === 'active' ? 'rgba(52,211,153,0.14)' : 'var(--os-accent-soft)', color: affiliate.status === 'approved' || affiliate.status === 'active' ? '#34D399' : 'var(--os-accent)' }}>
            {(affiliate.status || '').toUpperCase()}
          </span>
        </p>
        <div className="mt-3 flex items-center gap-2">
          <code className="flex-1 truncate rounded-xl px-4 py-3 text-[13px] font-semibold"
            style={{ background: 'var(--os-surface-2)', color: 'var(--os-ink)', border: '1px solid var(--os-border)' }}>{link}</code>
          <button onClick={copy} data-testid="affiliate-copy-link"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-3 text-[12.5px] font-bold text-white transition-all hover:opacity-90"
            style={{ background: copied ? '#34D399' : '#0EA5E9' }}>
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? (isEs ? 'Copiado' : 'Copied') : (isEs ? 'Copiar' : 'Copy')}
          </button>
        </div>
      </OsCard>
      <OsCard className="mt-3 p-5" data-testid="affiliate-demo-link-card">
        <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold" style={{ color: 'var(--os-accent)' }}>
          <MousePointerClick className="w-3.5 h-3.5" /> {isEs ? 'Tu demo con tu código — envíala a clientes' : 'Your demo with your code — send it to clients'}
        </p>
        <p className="mt-1.5 text-[12.5px]" style={{ color: 'var(--os-ink-soft)' }}>
          {isEs ? 'Abre el Restaurant AI OS con datos de muestra. Cada visita queda registrada con tu código.' : 'Opens the Restaurant AI OS with sample data. Every visit is tracked to your code.'}
        </p>
        <div className="mt-3 flex items-center gap-2">
          <code className="flex-1 truncate rounded-xl px-4 py-3 text-[13px] font-semibold"
            style={{ background: 'var(--os-surface-2)', color: 'var(--os-ink)', border: '1px solid var(--os-border)' }}>{demoLink}</code>
          <button onClick={copyDemo} data-testid="affiliate-copy-demo-link"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-3 text-[12.5px] font-bold text-white transition-all hover:opacity-90"
            style={{ background: copiedDemo ? '#34D399' : 'var(--os-accent)' }}>
            {copiedDemo ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copiedDemo ? (isEs ? 'Copiado' : 'Copied') : (isEs ? 'Copiar' : 'Copy')}
          </button>
        </div>
      </OsCard>
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={MousePointerClick} label="Clicks"><AnimatedNumber value={affiliate.clicks} /></Stat>
        <Stat icon={Users} label={isEs ? 'Referidos' : 'Referrals'} delay={60}><AnimatedNumber value={affiliate.referrals} /></Stat>
        <Stat icon={Wallet} label={isEs ? 'Pendiente' : 'Pending'} delay={120}><AnimatedNumber value={affiliate.pending} format={money} /></Stat>
        <Stat icon={BadgeDollarSign} label={isEs ? 'Pagado' : 'Paid'} delay={180}><AnimatedNumber value={affiliate.paid} format={money} /></Stat>
      </div>
      <AgentAdviceRail audience="affiliate" isEs={isEs} className="mt-5" onCardClick={() => enter({ id: 'affiliate' })} />
      <button onClick={() => enter({ id: 'affiliate' })} data-testid="affiliate-enter-btn"
        className="mt-6 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[14px] font-bold text-white transition-all hover:opacity-90 os3-lift"
        style={{ background: '#0EA5E9' }}>
        {isEs ? 'Abrir mi Affiliate Center' : 'Open my Affiliate Center'} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
